// Figures in a document: drawings, Mermaid diagrams, and workflow diagrams.
// Pure string functions, so SSR, the element and the editor's preview draw the
// same thing. The source stays the document: a drawing is an ```svg fence, a
// diagram a ```mermaid fence, a workflow its `steps:` — the agent receives that
// text exactly as written (fences are delivered verbatim), and readers see it
// drawn.
//
//   drawings  sanitized here to a small SVG vocabulary (shapes, paths, text; no
//             scripts, links, styles, images, foreign content or event handlers)
//   mermaid   the source is shown until a Mermaid runtime draws it, on demand
//             (diagrams.ts) — the renderer bundle never contains Mermaid
//   workflow  laid out here: steps in layers by their `depends_on`, drawn as
//             boxes and arrows, with the condition of a gated step on hover
import { parse as parseYaml } from "yaml";
import { escapeHtml as e } from "./md";

// ---- drawings: an allow-list SVG sanitizer -----------------------------------------
const ELEMENTS = new Set(["svg", "g", "path", "line", "polyline", "polygon", "rect", "circle", "ellipse", "text", "title", "desc"]);
const NUM = /^-?\d*\.?\d+(?:e-?\d+)?$/i;
const COLOR = /^(?:#[0-9a-f]{3,8}|none|currentColor|transparent|[a-z]{3,20})$/i;
const ATTRS: Record<string, RegExp> = {
  viewBox: /^[-\d.\s,e]+$/i, width: NUM, height: NUM, x: NUM, y: NUM, x1: NUM, y1: NUM, x2: NUM, y2: NUM, cx: NUM, cy: NUM, r: NUM, rx: NUM, ry: NUM,
  d: /^[MLHVCSQTAZmlhvcsqtaz\d.,\s-]*$/, points: /^[\d.,\s-]*$/,
  fill: COLOR, stroke: COLOR, "stroke-width": NUM, "stroke-linecap": /^(?:butt|round|square)$/, "stroke-linejoin": /^(?:miter|round|bevel)$/,
  "stroke-dasharray": /^[\d.,\s]+$/, opacity: NUM, "fill-opacity": NUM, "stroke-opacity": NUM,
  "font-size": NUM, "font-weight": /^(?:normal|bold|[1-9]00)$/, "text-anchor": /^(?:start|middle|end)$/, "dominant-baseline": /^(?:auto|middle|central|hanging)$/,
  transform: /^(?:(?:translate|scale|rotate)\([-\d.,\s]+\)\s*)+$/,
};
const unescape = (s: string) => s.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#x27;|&#39;/g, "'").replace(/&amp;/g, "&");

/** The drawing, reduced to the allowed vocabulary, and its title; null when it is not an SVG. */
export function sanitizeSvg(source: string): { svg: string; title: string } | null {
  const text = source.replace(/<!--[\s\S]*?-->/g, "").trim();
  if (!/^<svg[\s>]/i.test(text) || text.length > 200_000) return null;
  let out = "", title = "", depth = 0, skip = 0, textOpen = "";
  const re = /<(\/?)([a-zA-Z][\w:-]*)((?:\s+[\w:-]+\s*=\s*(?:"[^"]*"|'[^']*'))*)\s*(\/?)>|([^<]+)/g;
  for (let m; (m = re.exec(text)); ) {
    const [, close, rawName, rawAttrs, selfClose, chars] = m;
    if (chars !== undefined) {
      if (!skip && (textOpen === "text" || textOpen === "title" || textOpen === "desc")) { const t = e(unescape(chars)); out += t; if (textOpen === "title") title += unescape(chars); }
      continue;
    }
    const name = rawName.toLowerCase() === "svg" ? "svg" : rawName;
    if (!ELEMENTS.has(name)) { if (!selfClose) skip += close ? -1 : 1; skip = Math.max(0, skip); continue; }
    if (skip) continue;
    if (close) { out += `</${name}>`; depth--; textOpen = ""; continue; }
    let attrs = "";
    for (const a of rawAttrs.matchAll(/([\w:-]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g)) {
      // (own properties only: an attribute named constructor or __proto__ is not one of the table's)
      const k = a[1], v = unescape(a[2] ?? a[3] ?? "").trim(), rule = Object.hasOwn(ATTRS, k) ? ATTRS[k] : undefined;
      if (rule && rule.test(v)) attrs += ` ${k}="${e(v)}"`;
    }
    if (name === "svg") attrs += ` role="img"`;
    out += `<${name}${attrs}${selfClose ? "/" : ""}>`;
    if (!selfClose) { depth++; textOpen = name; }
  }
  if (depth > 0) return null;
  return { svg: out, title: title.trim() };
}


// ---- workflows: a layered drawing of the steps ---------------------------------------
interface Step { name: string; kind: string; deps: string[]; when?: string; detail?: string }

function steps(yamlText: string): Step[] | null {
  let doc: unknown;
  try { doc = parseYaml(yamlText); } catch { return null; }
  const raw = (doc as { steps?: unknown } | null)?.steps;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const out: Step[] = [];
  for (const [name, v] of Object.entries(raw as Record<string, unknown>)) {
    const s = (v ?? {}) as Record<string, unknown>;
    const deps = Array.isArray(s.depends_on) ? s.depends_on.map(String) : typeof s.depends_on === "string" ? [s.depends_on] : [];
    const detail = [s.tool && `${s.server ? `${s.server}.` : ""}${s.tool}`, s.question, s.instruction, s.cron && `cron ${s.cron}`, s.on && `on ${s.on}`].find((x) => typeof x === "string") as string | undefined;
    out.push({ name, kind: String(s.kind ?? "step"), deps, when: typeof s.when === "string" ? s.when : undefined, detail });
  }
  return out.length ? out : null;
}

const GROUP = (k: string) => (k === "human" ? "human" : k === "agent" ? "agent" : k === "finish" ? "end" : /^(manual|schedule|event|webhook)$/.test(k) ? "start" : "tool");
const clipText = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** The workflow's steps drawn as a top-to-bottom graph, or null when it has none. */
export function workflowSvg(yamlText: string, name = "workflow"): string | null {
  const list = steps(yamlText);
  if (!list) return null;
  const by = new Map(list.map((s) => [s.name, s]));
  // layer = longest path from a start; a cycle (invalid anyway) is cut where found
  const layer = new Map<string, number>();
  const visit = (s: Step, seen: Set<string>): number => {
    if (layer.has(s.name)) return layer.get(s.name)!;
    if (seen.has(s.name)) return 0;
    seen.add(s.name);
    const l = s.deps.reduce((m, d) => (by.has(d) ? Math.max(m, visit(by.get(d)!, seen) + 1) : m), 0);
    layer.set(s.name, l);
    return l;
  };
  list.forEach((s) => visit(s, new Set()));
  const layers: Step[][] = [];
  list.forEach((s) => (layers[layer.get(s.name)!] ??= []).push(s));
  // order each layer by where its parents sit, so edges cross less
  const col = new Map<string, number>();
  layers.forEach((row) => {
    row.sort((a, b) => avg(a) - avg(b));
    row.forEach((s, i) => col.set(s.name, i - (row.length - 1) / 2));
  });
  function avg(s: Step) { const p = s.deps.filter((d) => col.has(d)).map((d) => col.get(d)!); return p.length ? p.reduce((a, b) => a + b, 0) / p.length : 0; }

  const W = 176, H = 44, GX = 20, GY = 34, PAD = 6;
  const widest = Math.max(...layers.map((r) => r.length));
  const width = widest * W + (widest - 1) * GX + PAD * 2;
  const height = layers.length * H + (layers.length - 1) * GY + PAD * 2;
  const pos = (n: string) => ({ x: width / 2 + col.get(n)! * (W + GX) - W / 2, y: PAD + layer.get(n)! * (H + GY) });

  let edges = "", nodes = "";
  for (const s of list) {
    const b = pos(s.name);
    for (const d of s.deps) {
      if (!by.has(d)) continue;
      const a = pos(d), x1 = a.x + W / 2, y1 = a.y + H, x2 = b.x + W / 2, y2 = b.y - 5, my = (y1 + y2) / 2;
      edges += `<path class="e${s.when ? " cond" : ""}" d="M${x1} ${y1}C${x1} ${my} ${x2} ${my} ${x2} ${y2}"/><path class="ah" d="M${x2 - 4} ${y2 - 1}L${x2} ${y2 + 5}L${x2 + 4} ${y2 - 1}Z"/>`;
    }
    const tip = [`${s.name} (${s.kind})`, s.when && `when ${s.when}`, s.detail].filter(Boolean).join("\n");
    nodes += `<g class="n ${GROUP(s.kind)}${s.when ? " cond" : ""}" transform="translate(${b.x} ${b.y})"><title>${e(tip)}</title><rect width="${W}" height="${H}" rx="8"/>`
      + `<text x="12" y="19" class="nm">${e(clipText(s.name, 22))}</text><text x="12" y="34" class="kd">${e(clipText(s.kind + (s.when ? " · if" : ""), 26))}</text></g>`;
  }
  return `<svg viewBox="0 0 ${width} ${height}" width="${width}" role="img" aria-label="${e(`Workflow ${name}: ${list.length} steps`)}">${edges}${nodes}</svg>`;
}
